import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import ValueWithIcon from "../../common/contacts/ValueWithIcon";
import { ContactFilterCriteriaProps } from "../../../pages/admin/Contacts";
import { useTranslation } from "react-i18next";
import { Avatar, DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import ContactType from "../../../enums/ContactType";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import { GET_CONTACTS } from "../../../graphql/contact";
import RelationshipType from "../../../enums/RelationshipType";
import RenderRelationship from "../../common/contacts/RenderRelationship";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Building03Icon,
  Call02Icon,
  Delete02Icon,
  Edit04Icon,
  Location06Icon,
  Mail01Icon,
  MailUpload01Icon,
  ViewIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { InviteContactModal, RemoveContactModal } from "./ContactModules";
import { fullName } from "utils/fullName";

type ContactAddress = {
  addressName: string | null;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  country: string;
  zipcode: string;
};

/** One row, and every field `extractContacts` writes. */
export type ContactRow = {
  id: string;
  name: string;
  phone: string;
  location: string;
  email: string;
  businessName: string | null;
  website: string | null;
  address: ContactAddress | null;
  status: string;
  contactType: ContactType;
  notes: string | null;
  parentContactId: string | null;
  relationshipType: RelationshipType;
};

type ContactNode = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string;
  website: string | null;
  businessName: string | null;
  contactType: ContactType;
  relationshipType: RelationshipType;
  address: ContactAddress | null;
  status: string;
  notes: string | null;
  parentContactId: string | null;
};

type ContactsQueryResult = Record<
  string,
  { edges: { node: ContactNode }[] } | undefined
>;

type ContactTableProps = {
  filterCriteria: ContactFilterCriteriaProps;
  refreshContacts?: number;
  handleOpenViewDetailsDrawer: (concactId: string) => void;
  handleOpenEditDetailsDrawer: (contactId: string) => void;
  handleRefreshContacts: () => void;
};

const ContactsTable: React.FC<ContactTableProps> = (props) => {
  const {
    handleOpenViewDetailsDrawer,
    filterCriteria,
    refreshContacts,
    handleOpenEditDetailsDrawer,
    handleRefreshContacts,
  } = props;

  const { t } = useTranslation();

  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );
  const [openRemoveContactModal, setOpenRemoveContactModal] = useState(false);
  const [openInviteContactModal, setOpenInviteContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactRow>();

  useEffect(() => {
    if (filterCriteria && Object.keys(filterCriteria).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        contactType: filterCriteria.contactType,
        status: filterCriteria.status,
        verificationStatus: filterCriteria.verificationStatus,
        searchTerm: filterCriteria.searchTerm,
      });
    }
  }, [filterCriteria]);

  const contactType = filterCriteria.contactType as ContactType;

  // `relationshipType` stays the raw enum: the row is data, and translating it
  // here both lies about the type and passes a stored value through a bare t().
  const extractContacts = (data: ContactsQueryResult): ContactRow[] => {
    const firstKey = Object.keys(data)[0];
    return (data[firstKey]?.edges ?? []).map(({ node }) => ({
      id: node.id,
      phone: node.phone,
      name: fullName(node),
      location: node.address
        ? `${node.address.city}, ${node.address.state}`
        : "",
      businessName: node.businessName,
      address: node.address,
      status: node.status,
      contactType: node.contactType,
      email: node.email,
      notes: node.notes,
      website: node.website,
      parentContactId: node.parentContactId,
      relationshipType: node.relationshipType,
    }));
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_CONTACTS,
      initialVariables: defaultPaginationVariables,
      extractData: extractContacts,
      refetchIndex: refreshContacts,
    });

  const columns: DataColumn<ContactRow>[] = useMemo(() => {
    const businessNameCell = (contact: ContactRow) =>
      contact.website ? (
        <Link
          href={
            contact.website.startsWith("http")
              ? contact.website
              : `http://${contact.website}`
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          underline="hover"
          sx={{ color: "inherit" }}
        >
          {contact.businessName}
        </Link>
      ) : (
        contact.businessName
      );

    const phone: DataColumn<ContactRow> = {
      id: "phone",
      header: t(
        contactType === ContactType.Individual
          ? "Contact Phone"
          : "Primary Phone",
      ),
      cell: (contact) => (
        <ValueWithIcon icon={Call02Icon}>{contact.phone}</ValueWithIcon>
      ),
    };
    const location: DataColumn<ContactRow> = {
      id: "location",
      header: t(
        contactType === ContactType.Individual
          ? "Contact Location"
          : "Company Location",
      ),
      cell: (contact) => (
        <ValueWithIcon icon={Location06Icon}>{contact.location}</ValueWithIcon>
      ),
    };
    const email: DataColumn<ContactRow> = {
      id: "email",
      header: t(
        contactType === ContactType.Individual
          ? "Contact Email"
          : "Primary Email",
      ),
      cell: (contact) => (
        <ValueWithIcon icon={Mail01Icon}>{contact.email}</ValueWithIcon>
      ),
    };
    const relationship: DataColumn<ContactRow> = {
      id: "relationshipType",
      header: t("Relationship"),
      cell: (contact) => (
        <RenderRelationship relationship={contact.relationshipType} />
      ),
    };

    if (contactType === ContactType.Individual) {
      return [
        {
          id: "name",
          header: t("Contact Name"),
          maxWidth: 200,
          cell: (contact) => ({
            avatar: <Avatar size="sm" name={contact.name} />,
            content: contact.name,
          }),
        },
        phone,
        location,
        {
          id: "company",
          header: t("Company"),
          cell: (contact) => (
            <ValueWithIcon icon={Building03Icon}>
              {contact.businessName}
            </ValueWithIcon>
          ),
        },
        email,
        relationship,
      ];
    }

    return [
      {
        id: "company",
        header: t("Company Name"),
        maxWidth: 200,
        cell: (contact) => ({
          avatar: <Avatar size="sm" name={contact.businessName ?? undefined} />,
          content: businessNameCell(contact),
        }),
      },
      phone,
      location,
      email,
      relationship,
    ];
  }, [t, contactType]);

  const rowActions = (contact: ContactRow): RowAction[] => [
    {
      label: t("View Contact Details"),
      icon: <HugeiconsIcon icon={ViewIcon} size={16} />,
      onClick: () => handleOpenViewDetailsDrawer(contact.id),
    },
    {
      label: t("Edit Contact Details"),
      icon: <HugeiconsIcon icon={Edit04Icon} size={16} />,
      onClick: () => handleOpenEditDetailsDrawer(contact.id),
    },
    {
      label: t("Remove Contact"),
      icon: <HugeiconsIcon icon={Delete02Icon} size={16} />,
      onClick: () => {
        setSelectedContact(contact);
        setOpenRemoveContactModal(true);
      },
    },
    {
      label: t("Send Invite"),
      icon: <HugeiconsIcon icon={MailUpload01Icon} size={16} />,
      onClick: () => {
        setSelectedContact(contact);
        setOpenInviteContactModal(true);
      },
    },
  ];

  return (
    <Box sx={{ height: "100%" }}>
      <DataTable<ContactRow>
        columns={columns}
        rows={dataArray}
        getRowKey={(contact) => contact.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(contact) => handleOpenViewDetailsDrawer(contact.id)}
        onScroll={handleScroll}
        emptyState={t("No contacts found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more contacts")}
        stickyHeader
      />

      <RemoveContactModal
        openRemoveContactModal={openRemoveContactModal}
        setOpenRemoveContactModal={setOpenRemoveContactModal}
        contactId={selectedContact?.id ?? ""}
        contactEmail={selectedContact?.email ?? ""}
        refreshContacts={handleRefreshContacts}
      />

      <InviteContactModal
        openInviteContactModal={openInviteContactModal}
        setOpenInviteContactModal={setOpenInviteContactModal}
        contact={selectedContact}
      />
    </Box>
  );
};

export default ContactsTable;
